import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import TierGate from './TierGate';
import { apiFetch, getApiHost } from '../utils/api';
import { SupplyControlIcons } from './SupplyControlIcons';
import { TokenIcon } from './TokenIcon';
import { useAuth } from '../contexts/AuthContext';
import { hasWatcherAccess } from '../utils/tiers';
import trendingPreviewData from '../data/trendingPreview.json';
import { trackEvent } from '../utils/analytics';

// ── Types ──────────────────────────────────────────────────────────────────

type SafetyLevel = 'OK' | 'RISKY' | 'DANGEROUS' | 'UNTRACKED';
type TimeRange = '5m' | '1h' | '4h' | '24h';
type SortCol = 'score' | 'price' | 'change' | 'volume' | 'txns' | 'mcap' | 'liquidity' | 'holders' | 'age' | 'lockup';
type SortDir = 'asc' | 'desc';

interface TrendingToken {
  address: string;
  name: string;
  symbol: string;
  logo_uri: string;
  price: number;
  market_cap: number;
  fdv: number;
  liquidity: number;
  volume_5m_usd: number;
  volume_1h_usd: number;
  volume_4h_usd: number;
  volume_24h_usd: number;
  price_change_5m_percent: number;
  price_change_1h_percent: number;
  price_change_4h_percent: number;
  price_change_24h_percent: number;
  trade_5m_count: number;
  trade_1h_count: number;
  trade_4h_count: number;
  trade_24h_count: number;
  volume_5m_change_percent: number;
  volume_1h_change_percent: number;
  volume_4h_change_percent: number;
  volume_24h_change_percent: number;
  unique_wallet_24h: number;
  buy_24h: number;
  sell_24h: number;
  holder: number;
  recent_listing_time: number;
  safetyLevel: SafetyLevel;
  trending_score_5m: number;
  trending_score_1h: number;
  trending_score_4h: number;
  trending_score_24h: number;
  score_components_5m?: ScoreComponents;
  score_components_1h?: ScoreComponents;
  score_components_4h?: ScoreComponents;
  score_components_24h?: ScoreComponents;
  // Supply-control percentages (% of supply, all default 0).
  // lockup_percent = locked + staked + multisig (excludes burn).
  locked_percent?: number;
  staked_percent?: number;
  multisig_percent?: number;
  multisig_vault_count?: number;
  multisig_threshold?: number;
  multisig_member_count?: number;
  burn_percent?: number;
  lockup_percent?: number;
  // Charity-coin enrichment from donate.gg. Presence of charityName
  // means the token routes creator-fee revenue to a real charity.
  charityName?: string;
  charityDonationSource?: string;
}

interface ScoreComponents {
  volume: number;
  acceleration: number;
  txns: number;
  wallets?: number;
  buyPressure?: number;
  /** Multiplicative price-direction factor (0..1). 1 means no
   *  penalty; lower means the token is dropping and the additive
   *  score is reduced. Risers always sit at 1.0. */
  priceFactor?: number;
}

// ── Map sort columns to API view names ────────────────────────────────────

const SORT_TO_VIEW: Record<SortCol, { view: string; defaultDir: SortDir }> = {
  score:     { view: 'trending',   defaultDir: 'desc' },
  price:     { view: 'price',      defaultDir: 'desc' },
  change:    { view: 'gainers',    defaultDir: 'desc' },
  volume:    { view: 'volume',     defaultDir: 'desc' },
  txns:      { view: 'txns',       defaultDir: 'desc' },
  mcap:      { view: 'market_cap', defaultDir: 'desc' },
  liquidity: { view: 'liquidity',  defaultDir: 'desc' },
  holders:   { view: 'holders',    defaultDir: 'desc' },
  age:       { view: 'new_tokens', defaultDir: 'desc' },
  lockup:    { view: 'lockup',     defaultDir: 'desc' },
};

const VIEW_TO_SORT: Record<string, SortCol> = Object.fromEntries(
  Object.entries(SORT_TO_VIEW).map(([col, { view }]) => [view, col as SortCol])
);

// ── Constants ──────────────────────────────────────────────────────────────

const API_HOST = getApiHost();

// Only "Trending" stays as a tab — Volume / Market Cap / Gainers were
// redundant with the column-header sorts. "Trending" remains because
// the score sort isn't reachable from any column (it's a composite),
// and it doubles as "reset to default sort".
const VIEW_TABS: { sort: SortCol; label: string }[] = [
  { sort: 'score', label: 'Trending' },
];

// "New Tokens" is a filter (maxAge=24h), not a sort. Lives in its own
// toggle button alongside Filter so it doesn't collide with the
// mutually-exclusive sort tabs above. Age column header still sorts.
const NEW_TOKENS_MAX_AGE_HOURS = 24;

const TIME_RANGES: { key: TimeRange; label: string }[] = [
  { key: '5m', label: '5m' },
  { key: '1h', label: '1h' },
  { key: '4h', label: '4h' },
  { key: '24h', label: '24h' },
];

// ── Formatters ─────────────────────────────────────────────────────────────

function formatUsd(n: number): string {
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(1)}K`;
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n > 0) return `$${n.toPrecision(3)}`;
  return '$0';
}

function formatPriceString(n: number): string {
  if (n >= 1) return `$${n.toFixed(2)}`;
  if (n >= 0.01) return `$${n.toFixed(4)}`;
  if (n > 0) return `$${n.toPrecision(3)}`;
  return '$0';
}

const FormatPrice: React.FC<{ value: number }> = ({ value }) => {
  if (value >= 0.01 || value <= 0) return <>{formatPriceString(value)}</>;

  const str = value.toFixed(20);
  const match = str.match(/^0\.(0+)/);
  if (!match || match[1].length < 4) return <>{formatPriceString(value)}</>;

  const zeroCount = match[1].length;
  const significantStart = 2 + zeroCount;
  const significant = str.slice(significantStart, significantStart + 4).replace(/0+$/, '');

  return (
    <span>$0.0<sub className="text-[0.8em]">{zeroCount}</sub>{significant}</span>
  );
};

function formatPct(n: number): string {
  const sign = n >= 0 ? '+' : '';
  return `${sign}${n.toFixed(1)}%`;
}

function formatNumber(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(1)}K`;
  return n.toLocaleString();
}

function formatAge(timestamp: number): string {
  if (!timestamp) return '—';
  const diffMs = Date.now() - timestamp * 1000;
  const diffMin = Math.floor(diffMs / 60000);
  const diffHrs = Math.floor(diffMin / 60);
  const diffDays = Math.floor(diffHrs / 24);
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHrs < 24) return `${diffHrs}h`;
  return `${diffDays}d`;
}

function timeAgo(ms: number): string {
  const diffSec = Math.floor((Date.now() - ms) / 1000);
  if (diffSec < 5) return 'just now';
  if (diffSec < 60) return `${diffSec}s ago`;
  return `${Math.floor(diffSec / 60)}m ago`;
}

// ── Time-range accessors ──────────────────────────────────────────────────

function getVolume(t: TrendingToken, range: TimeRange): number {
  const m: Record<TimeRange, number> = { '5m': t.volume_5m_usd, '1h': t.volume_1h_usd, '4h': t.volume_4h_usd, '24h': t.volume_24h_usd };
  return m[range];
}

function getPriceChange(t: TrendingToken, range: TimeRange): number {
  const m: Record<TimeRange, number> = { '5m': t.price_change_5m_percent, '1h': t.price_change_1h_percent, '4h': t.price_change_4h_percent, '24h': t.price_change_24h_percent };
  return m[range];
}

function getTradeCount(t: TrendingToken, range: TimeRange): number {
  const m: Record<TimeRange, number> = { '5m': t.trade_5m_count, '1h': t.trade_1h_count, '4h': t.trade_4h_count, '24h': t.trade_24h_count };
  return m[range];
}

function getTrendingScore(t: TrendingToken, range: TimeRange): number {
  const m: Record<TimeRange, number> = { '5m': t.trending_score_5m, '1h': t.trending_score_1h, '4h': t.trending_score_4h, '24h': t.trending_score_24h };
  return m[range];
}

function getScoreComponents(t: TrendingToken, range: TimeRange): ScoreComponents | undefined {
  const m: Record<TimeRange, ScoreComponents | undefined> = { '5m': t.score_components_5m, '1h': t.score_components_1h, '4h': t.score_components_4h, '24h': t.score_components_24h };
  return m[range];
}

function getVolChangePercent(t: TrendingToken, range: TimeRange): number {
  const m: Record<TimeRange, number> = { '5m': t.volume_5m_change_percent, '1h': t.volume_1h_change_percent, '4h': t.volume_4h_change_percent, '24h': t.volume_24h_change_percent };
  return m[range];
}

// ── Filter popup ──────────────────────────────────────────────────────────

interface Filters {
  minLiquidity: string;
  maxLiquidity: string;
  minMcap: string;
  maxMcap: string;
  minHolders: string;
  maxHolders: string;
  minVolume: string;
  maxVolume: string;
  minLockup: string;
  maxLockup: string;
}

const EMPTY_FILTERS: Filters = {
  minLiquidity: '', maxLiquidity: '',
  minMcap: '', maxMcap: '',
  minHolders: '', maxHolders: '',
  minVolume: '', maxVolume: '',
  minLockup: '', maxLockup: '',
};

function hasActiveFilters(f: Filters): boolean {
  return Object.values(f).some(v => v !== '');
}

const FilterPopup: React.FC<{
  filters: Filters;
  onChange: (f: Filters) => void;
  onClose: () => void;
}> = ({ filters, onChange, onClose }) => {
  const [draft, setDraft] = useState<Filters>(filters);
  const set = (key: keyof Filters, val: string) => setDraft(prev => ({ ...prev, [key]: val }));

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-24" onClick={onClose}>
      <div className="absolute inset-0 bg-black/60" />
      <div className="relative bg-crypto-dark border border-gray-700 rounded-xl p-5 w-full max-w-sm shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold">Filter</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl leading-none">&times;</button>
        </div>
        <div className="space-y-3">
          {([
            ['Liquidity', 'minLiquidity', 'maxLiquidity', 'K'],
            ['Market Cap', 'minMcap', 'maxMcap', 'K'],
            ['Holders', 'minHolders', 'maxHolders', ''],
            ['Volume', 'minVolume', 'maxVolume', 'K'],
            ['Lockup', 'minLockup', 'maxLockup', '%'],
          ] as [string, keyof Filters, keyof Filters, string][]).map(([label, minKey, maxKey, unit]) => (
            <div key={label} className="flex items-center gap-2">
              <span className="text-sm text-gray-400 w-20 flex-shrink-0">{label}</span>
              <div className="flex-1 flex gap-2">
                <input type="number" placeholder="Min" value={draft[minKey]}
                  onChange={e => set(minKey, e.target.value)}
                  className="w-full bg-crypto-gray border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-crypto-accent" />
                <input type="number" placeholder="Max" value={draft[maxKey]}
                  onChange={e => set(maxKey, e.target.value)}
                  className="w-full bg-crypto-gray border border-gray-700 rounded-lg px-2 py-1.5 text-sm text-white focus:outline-none focus:border-crypto-accent" />
              </div>
              {unit && <span className="text-xs text-gray-500 w-4">{unit}</span>}
            </div>
          ))}
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={() => {
              // Report which dimensions were filtered, not the numbers — the
              // question is which levers people reach for, and raw thresholds
              // would be high-cardinality noise.
              trackEvent('trending_filter_applied', {
                fields: Object.entries(draft).filter(([, v]) => v !== '').map(([k]) => k),
                fieldCount: Object.values(draft).filter(v => v !== '').length,
              });
              onChange(draft); onClose();
            }}
            className="flex-1 bg-crypto-accent text-crypto-dark py-2 rounded-lg font-medium text-sm hover:brightness-110 transition">
            Apply
          </button>
          <button onClick={() => { trackEvent('trending_filter_cleared'); onChange(EMPTY_FILTERS); onClose(); }}
            className="px-4 py-2 rounded-lg text-sm text-gray-400 hover:text-white border border-gray-700 transition">
            Clear
          </button>
        </div>
      </div>
    </div>
  );
};

// ── Component ──────────────────────────────────────────────────────────────

// Temporary public-access window for the trending feed. Anyone (no
// account, no Watcher tier) can view it through this date. To extend
// or shorten, change the constant and redeploy; to restore the
// permanent gate, set it to a past date.
const PUBLIC_TRENDING_UNTIL = new Date('2026-05-28T23:59:59Z');

// Bundled snapshot of the live feed, captured once and shipped with the app.
// Shown (dimmed + labelled "old") behind the login gate so logged-out visitors
// can see what the page looks like without us serving the live (paid) feed free.
const PREVIEW_SNAPSHOT = trendingPreviewData as unknown as { capturedAt: number; tokens: TrendingToken[] };

const TrendingPage: React.FC = () => {
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  const isPubliclyOpen = Date.now() < PUBLIC_TRENDING_UNTIL.getTime();

  if (authLoading) {
    return (
      <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
        <Header />
        <main className="flex-1 flex items-center justify-center">
          <div className="animate-spin w-8 h-8 border-2 border-crypto-accent border-t-transparent rounded-full" />
        </main>
        <Footer />
      </div>
    );
  }
  if (!isPubliclyOpen && (!isAuthenticated || !hasWatcherAccess(user?.tier))) {
    return <TrendingPreviewGate />;
  }
  return <TrendingPageContent />;
};

// Gated view: the real trending layout rendered from the OLD bundled snapshot,
// dimmed and non-interactive, with the login gate front-and-centre over it.
// No live fetch happens here, so the paid feed is never exposed to gated users.
const TrendingPreviewGate: React.FC = () => (
  <div className="min-h-screen bg-crypto-dark text-white flex flex-col">
    {/* Crisp, interactive chrome OUTSIDE the blur — its LoginModal must not be
        trapped in the dimmed/non-interactive background layer. */}
    <Header />
    <div className="relative flex-1">
      <div aria-hidden className="pointer-events-none select-none blur-[1.5px] opacity-55 max-h-[calc(100vh-4rem)] overflow-hidden">
        <TrendingPageContent preview={PREVIEW_SNAPSHOT} />
      </div>
      <div className="absolute inset-0 z-30 flex items-start justify-center px-4 pt-10 sm:pt-16 bg-crypto-dark/50 backdrop-blur-[1px]">
        <div className="w-full max-w-md">
          <TierGate required="watcher" feature="live trending" />
          <p className="text-center text-xs text-gray-500 mt-3">
            The table behind this is an old sample snapshot — log in for the live feed.
          </p>
        </div>
      </div>
    </div>
    <Footer />
  </div>
);

const TrendingPageContent: React.FC<{ preview?: { capturedAt: number; tokens: TrendingToken[] } }> = ({ preview }) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const timeRange = (searchParams.get('range') as TimeRange) || '24h';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const viewParam = searchParams.get('view') || 'trending';
  const dirParam = (searchParams.get('dir') as SortDir) || '';
  const maxAgeHoursParam = parseFloat(searchParams.get('maxAge') || '');
  const newTokensActive = !isNaN(maxAgeHoursParam) && maxAgeHoursParam > 0;

  const activeSort: SortCol = VIEW_TO_SORT[viewParam] || 'score';
  const activeDir: SortDir = dirParam || SORT_TO_VIEW[activeSort]?.defaultDir || 'desc';

  // Filter state (client-side, applied after fetch — server filters could be added later)
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);

  // Data state. In preview mode we seed from the bundled snapshot and never fetch.
  const [tokens, setTokens] = useState<TrendingToken[]>(preview?.tokens ?? []);
  const [totalFiltered, setTotalFiltered] = useState(preview?.tokens.length ?? 0);
  const [hasNext, setHasNext] = useState(false);
  const [cachedAt, setCachedAt] = useState(preview?.capturedAt ?? 0);
  const [loading, setLoading] = useState(!preview);
  const [error, setError] = useState<string | null>(null);

  const [, setTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 5000);
    return () => clearInterval(timer);
  }, []);

  const updateParams = useCallback((updates: Record<string, string>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      for (const [k, v] of Object.entries(updates)) {
        if (v) next.set(k, v);
        else next.delete(k);
      }
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  const setPage = useCallback((p: number) => {
    updateParams({ page: p > 1 ? String(p) : '' });
  }, [updateParams]);

  // ── Column sort handler ───────────────────────────────────────────────

  const handleSort = useCallback((col: SortCol) => {
    const { view, defaultDir } = SORT_TO_VIEW[col];
    // Which column people actually sort by is the clearest signal of what they
    // come to /trending for. Autocapture records the click but keys it on the
    // button's DOM position and label, which shifts whenever the tabs change.
    trackEvent('trending_sorted', { column: col, wasActive: activeSort === col });
    if (activeSort === col) {
      const newDir = activeDir === 'desc' ? 'asc' : 'desc';
      updateParams({ view, dir: newDir === defaultDir ? '' : newDir, page: '' });
    } else {
      updateParams({ view, dir: '', page: '' });
    }
  }, [activeSort, activeDir, updateParams]);

  // ── Fetch data ─────────────────────────────────────────────────────────

  const hasData = useRef(!!preview);

  const fetchTrending = useCallback(async () => {
    if (preview) return;              // gated preview — never hit the live (paid) feed
    if (!hasData.current) setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ view: viewParam });
      if (timeRange !== '24h') params.set('range', timeRange);
      if (page > 1) params.set('page', String(page));
      if (dirParam && dirParam !== SORT_TO_VIEW[activeSort]?.defaultDir) {
        params.set('dir', dirParam);
      }
      // Server-side filters (values in K for liquidity/mcap/volume)
      if (filters.minLiquidity) params.set('minLiquidity', String(parseFloat(filters.minLiquidity) * 1000));
      if (filters.maxLiquidity) params.set('maxLiquidity', String(parseFloat(filters.maxLiquidity) * 1000));
      if (filters.minMcap) params.set('minMcap', String(parseFloat(filters.minMcap) * 1000));
      if (filters.maxMcap) params.set('maxMcap', String(parseFloat(filters.maxMcap) * 1000));
      if (filters.minHolders) params.set('minHolders', filters.minHolders);
      if (filters.maxHolders) params.set('maxHolders', filters.maxHolders);
      if (filters.minVolume) params.set('minVolume', String(parseFloat(filters.minVolume) * 1000));
      if (filters.maxVolume) params.set('maxVolume', String(parseFloat(filters.maxVolume) * 1000));
      if (filters.minLockup) params.set('minLockupPercent', filters.minLockup);
      if (filters.maxLockup) params.set('maxLockupPercent', filters.maxLockup);
      if (newTokensActive) params.set('maxAgeHours', String(maxAgeHoursParam));

      const res = await apiFetch(`${API_HOST}/api/trending?${params}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setTokens(data.tokens);
      setTotalFiltered(data.totalFiltered);
      setHasNext(data.hasNext);
      setCachedAt(data.cachedAt);
      hasData.current = true;
    } catch (err) {
      if (!hasData.current) setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [viewParam, timeRange, page, dirParam, activeSort, filters, newTokensActive, maxAgeHoursParam, preview]);

  useEffect(() => {
    fetchTrending();
  }, [fetchTrending]);

  // ── Poll for fresh data every 10s ──────────────────────────────────────

  const fetchRef = useRef(fetchTrending);
  fetchRef.current = fetchTrending;

  useEffect(() => {
    if (preview) return;             // no polling in gated preview
    const timer = setInterval(() => {
      fetchRef.current();
    }, 10_000);
    return () => clearInterval(timer);
  }, [preview]);

  const displayTokens = tokens;

  // ── Render helpers ────────────────────────────────────────────────────

  const SortArrow: React.FC<{ col: SortCol }> = ({ col }) => (
    <span className="ml-0.5 inline-block w-3 text-center">
      {activeSort === col ? (activeDir === 'desc' ? '▼' : '▲') : ''}
    </span>
  );

  const thClass = (col: SortCol) =>
    `py-1 px-3 text-xs font-medium whitespace-nowrap cursor-pointer select-none transition-colors bg-crypto-dark ${
      activeSort === col ? 'text-crypto-accent' : 'text-gray-500 hover:text-gray-300'
    }`;

  // ── Render ─────────────────────────────────────────────────────────────

  const body = (
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-2">
        {preview && (
          <div className="mb-2 flex items-center gap-2 rounded-lg border border-yellow-600/40 bg-yellow-600/10 px-3 py-2 text-xs text-yellow-200/90">
            <i className="fas fa-clock-rotate-left" />
            <span>Old sample snapshot from {new Date(preview.capturedAt).toLocaleDateString()} — not live. Log in to see the live trending feed.</span>
          </div>
        )}
        {/* Header: everything on one line. The view-tab pill row already
            says "Trending" (along with the active sort arrow), so a
            standalone <h1>Trending</h1> would just repeat it. */}
        <div className="flex items-center gap-3 mb-1.5 flex-wrap">
          <div className="flex gap-1">
            {VIEW_TABS.map(v => (
              <button key={v.sort} onClick={() => handleSort(v.sort)}
                className={`px-3 py-1 rounded-lg text-xs font-medium transition-colors ${
                  activeSort === v.sort
                    ? 'bg-crypto-accent text-crypto-dark'
                    : 'bg-crypto-gray text-gray-400 hover:text-white border border-gray-700'
                }`}>{v.label}<SortArrow col={v.sort} /></button>
            ))}
          </div>
          <div className="w-px h-4 bg-gray-700" />
          <div className="flex gap-1">
            {TIME_RANGES.map(tr => (
              <button key={tr.key}
                onClick={() => {
                  trackEvent('trending_range_changed', { range: tr.key });
                  updateParams({ range: tr.key === '24h' ? '' : tr.key, page: '' });
                }}
                className={`px-2.5 py-1 rounded text-xs font-medium transition-colors ${
                  timeRange === tr.key
                    ? 'bg-crypto-accent/20 text-crypto-accent border border-crypto-accent/40'
                    : 'bg-crypto-gray text-gray-400 hover:text-white border border-gray-700'
                }`}>{tr.label}</button>
            ))}
          </div>
          <button onClick={() => { trackEvent('trending_filter_opened', { hasActiveFilters: hasActiveFilters(filters) }); setShowFilters(true); }}
            className={`flex items-center gap-1.5 px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
              hasActiveFilters(filters)
                ? 'bg-crypto-accent/20 text-crypto-accent border-crypto-accent/40'
                : 'bg-crypto-gray text-gray-400 hover:text-white border-gray-700'
            }`}>
            <i className="fas fa-filter text-[10px]" /> Filter
            {hasActiveFilters(filters) && <span className="w-1.5 h-1.5 rounded-full bg-crypto-accent" />}
          </button>
          <button
            onClick={() => updateParams({ maxAge: newTokensActive ? '' : String(NEW_TOKENS_MAX_AGE_HOURS), page: '' })}
            title={`Show only tokens younger than ${NEW_TOKENS_MAX_AGE_HOURS}h`}
            className={`px-2.5 py-1 rounded text-xs font-medium border transition-colors ${
              newTokensActive
                ? 'bg-crypto-accent/20 text-crypto-accent border-crypto-accent/40'
                : 'bg-crypto-gray text-gray-400 hover:text-white border-gray-700'
            }`}>
            New Tokens
          </button>
          <div className="flex items-center gap-3 ml-auto">
            {totalFiltered > 0 && !loading && (
              <span className="text-xs text-gray-500">{totalFiltered} tokens</span>
            )}
            {preview ? (
              <span className="text-xs text-yellow-500/80">sample snapshot</span>
            ) : cachedAt > 0 ? (
              <span className="text-xs text-gray-500 flex items-center">
                <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500 mr-1 animate-pulse" />
                {timeAgo(cachedAt)}
              </span>
            ) : null}
          </div>
        </div>

        {showFilters && (
          <FilterPopup filters={filters} onChange={setFilters} onClose={() => setShowFilters(false)} />
        )}

        {/* Error */}
        {error && !loading && (
          <div className="text-center py-16">
            <i className="fas fa-exclamation-triangle text-red-400 text-4xl mb-4 block" />
            <p className="text-gray-400 mb-4">{error}</p>
            <button onClick={fetchTrending} className="bg-crypto-accent text-crypto-dark px-4 py-2 rounded-lg font-medium hover:brightness-110 transition">Retry</button>
          </div>
        )}

        {/* Loading */}
        {loading && (
          <div className="bg-crypto-gray border border-gray-700 rounded-xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-crypto-dark border-b border-gray-700">
                  <tr>
                    {['Token', 'MCap', 'Price', 'Age', 'Lockup %', `${timeRange} %`, `Vol ${timeRange}`, `Txns ${timeRange}`, 'Liquidity', 'Holders'].map(h => (
                      <th key={h} className="text-left py-1 px-3 text-xs font-medium text-gray-500 whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {Array.from({ length: 20 }).map((_, i) => (
                    <tr key={i} className="animate-pulse">
                      {Array.from({ length: 9 }).map((_, j) => (
                        <td key={j} className="py-1 px-3"><div className="h-4 w-12 bg-gray-700 rounded" /></td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && displayTokens.length === 0 && (
          <div className="text-center py-16 text-gray-500">
            <i className="fas fa-ghost text-4xl mb-4 block" />
            <p>No tokens found. Try adjusting your filters.</p>
          </div>
        )}

        {/* Table */}
        {!loading && !error && displayTokens.length > 0 && (
          <>
          <div className="bg-crypto-gray border border-gray-700 rounded-t-xl overflow-auto max-h-[calc(100vh-160px)]">
              <table className="w-full">
                <thead className="bg-crypto-dark border-b border-gray-700 sticky top-0 z-10">
                  <tr>
                    <th className="text-left py-1 px-3 text-xs font-medium text-gray-500 bg-crypto-dark">Token</th>
                    <th className={`text-right bg-crypto-dark ${thClass('mcap')}`} onClick={() => handleSort('mcap')}>MCap<SortArrow col="mcap" /></th>
                    <th className={`text-right ${thClass('price')}`} onClick={() => handleSort('price')}>Price<SortArrow col="price" /></th>
                    <th className={`text-right ${thClass('age')}`} onClick={() => handleSort('age')}>Age<SortArrow col="age" /></th>
                    <th className={`text-right ${thClass('lockup')}`} onClick={() => handleSort('lockup')} title="% of supply not immediately liquid: locked (Streamflow vesting), staked (Tibane), or held in multisig vaults. Reduces immediate-dump risk; not a guarantee of safety.">Lockup %<SortArrow col="lockup" /></th>
                    <th className={`text-right ${thClass('change')}`} onClick={() => handleSort('change')}>{timeRange} %<SortArrow col="change" /></th>
                    <th className={`text-right ${thClass('volume')}`} onClick={() => handleSort('volume')}>Vol {timeRange}<SortArrow col="volume" /></th>
                    <th className={`text-right ${thClass('txns')}`} onClick={() => handleSort('txns')}>Txns {timeRange}<SortArrow col="txns" /></th>
                    <th className={`text-right ${thClass('liquidity')}`} onClick={() => handleSort('liquidity')}>Liquidity<SortArrow col="liquidity" /></th>
                    <th className={`text-right ${thClass('holders')}`} onClick={() => handleSort('holders')}>Holders<SortArrow col="holders" /></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700/50">
                  {displayTokens.map((token, idx) => {
                    const sc = getScoreComponents(token, timeRange);
                    const weights = timeRange === '24h'
                      ? { volume: 30, acceleration: 20, txns: 20, wallets: 15, buyPressure: 15 }
                      : { volume: 45, acceleration: 30, txns: 25 };
                    return (
                      <tr key={token.address} className="hover:bg-crypto-dark/50 transition-colors">
                        <td className="py-1 px-3">
                          <div className="flex items-center gap-2">
                            <div className="relative group/score flex-shrink-0">
                              <Link to={`/token/${token.address}`}>
                                <TokenIcon
                                  src={token.logo_uri}
                                  symbol={token.symbol}
                                  name={token.name}
                                  sizeClass="w-9 h-9"
                                />
                              </Link>
                              {sc && (() => {
                                const vol = getVolume(token, timeRange);
                                const volChangePct = getVolChangePercent(token, timeRange);
                                const txns = getTradeCount(token, timeRange);
                                const buyTotal = token.buy_24h + token.sell_24h;
                                const buyRatio = buyTotal > 0 ? (token.buy_24h / buyTotal * 100).toFixed(0) : '50';
                                return (
                                  <div className="absolute left-0 top-full mt-1 z-50 hidden group-hover/score:block bg-crypto-dark border border-gray-600 rounded-lg p-3 text-left text-xs whitespace-nowrap shadow-xl min-w-[240px]">
                                    <div className="text-gray-400 font-normal mb-2">Trending Score: {getTrendingScore(token, timeRange)} ({timeRange})</div>
                                    <table className="w-full font-normal">
                                      <thead><tr className="text-gray-500"><th className="text-left pb-1">Signal</th><th className="text-right pb-1">Raw</th><th className="text-right pb-1">%ile</th><th className="text-right pb-1">Wt</th></tr></thead>
                                      <tbody className="text-white">
                                        <tr><td className="text-gray-400 pr-3 py-0.5">Volume</td><td className="text-right">{formatUsd(vol)}</td><td className="text-right text-crypto-accent">{sc.volume}</td><td className="text-right text-gray-500">{weights.volume}%</td></tr>
                                        <tr><td className="text-gray-400 pr-3 py-0.5">Vol Δ%</td><td className="text-right">{volChangePct != null ? `${volChangePct > 0 ? '+' : ''}${Number(volChangePct).toFixed(0)}%` : '—'}</td><td className="text-right text-crypto-accent">{sc.acceleration}</td><td className="text-right text-gray-500">{weights.acceleration}%</td></tr>
                                        <tr><td className="text-gray-400 pr-3 py-0.5">Txns</td><td className="text-right">{formatNumber(txns)}</td><td className="text-right text-crypto-accent">{sc.txns}</td><td className="text-right text-gray-500">{weights.txns}%</td></tr>
                                        {sc.wallets !== undefined && <tr><td className="text-gray-400 pr-3 py-0.5">Wallets</td><td className="text-right">{formatNumber(token.unique_wallet_24h)}</td><td className="text-right text-crypto-accent">{sc.wallets}</td><td className="text-right text-gray-500">{(weights as any).wallets}%</td></tr>}
                                        {sc.buyPressure !== undefined && <tr><td className="text-gray-400 pr-3 py-0.5">Buy %</td><td className="text-right">{buyRatio}%</td><td className="text-right text-crypto-accent">{sc.buyPressure}</td><td className="text-right text-gray-500">{(weights as any).buyPressure}%</td></tr>}
                                      </tbody>
                                    </table>
                                    {sc.priceFactor !== undefined && (() => {
                                      const pc = timeRange === '5m' ? token.price_change_5m_percent
                                        : timeRange === '1h' ? token.price_change_1h_percent
                                        : timeRange === '4h' ? token.price_change_4h_percent
                                        : token.price_change_24h_percent;
                                      const pcStr = pc != null ? `${pc > 0 ? '+' : ''}${pc.toFixed(0)}%` : '—';
                                      const factorColor = sc.priceFactor < 0.5 ? 'text-red-400' : sc.priceFactor < 1 ? 'text-yellow-400' : 'text-gray-400';
                                      return (
                                        <div className="mt-2 pt-2 border-t border-gray-700 text-[11px] flex justify-between text-gray-500">
                                          <span>Price Δ% (multiplier)</span>
                                          <span className="text-white">{pcStr} <span className={factorColor}>×{sc.priceFactor.toFixed(2)}</span></span>
                                        </div>
                                      );
                                    })()}
                                  </div>
                                );
                              })()}
                            </div>
                            <Link to={`/token/${token.address}`} className="min-w-0 group">
                              <span className="text-sm font-medium text-white group-hover:text-crypto-accent transition-colors truncate block max-w-[140px]">{token.name}</span>
                              <span className="text-xs text-gray-500 inline-flex items-center">
                                ${token.symbol}
                                {token.charityName && (
                                  <i
                                    className="fas fa-hand-holding-heart text-pink-400 ml-1.5"
                                    title={`Charity coin — fees routed via ${token.charityDonationSource ?? 'donate.gg'} (listed on donate.gg)`}
                                  />
                                )}
                                <SupplyControlIcons
                                  locked={token.locked_percent}
                                  staked={token.staked_percent}
                                  multisig={token.multisig_percent}
                                  burned={token.burn_percent}
                                  multisigVaultCount={token.multisig_vault_count}
                                  multisigThreshold={token.multisig_threshold}
                                  multisigMemberCount={token.multisig_member_count}
                                />
                              </span>
                            </Link>
                          </div>
                        </td>
                        <td className="py-1 px-3 text-sm text-right text-gray-300 whitespace-nowrap">{formatUsd(token.market_cap)}</td>
                        <td className="py-1 px-3 text-sm text-right text-white font-mono whitespace-nowrap"><FormatPrice value={token.price} /></td>
                        <td className="py-1 px-3 text-sm text-right text-gray-500 whitespace-nowrap">{formatAge(token.recent_listing_time)}</td>
                        <td className={`py-1 px-3 text-sm text-right whitespace-nowrap ${(token.lockup_percent ?? 0) > 0 ? 'text-gray-300' : 'text-gray-600'}`}>
                          {(token.lockup_percent ?? 0) > 0 ? `${(token.lockup_percent ?? 0).toFixed(1)}%` : '—'}
                        </td>
                        <td className={`py-1 px-3 text-sm text-right font-mono whitespace-nowrap ${getPriceChange(token, timeRange) >= 0 ? 'text-green-400' : 'text-red-400'}`}>
                          {formatPct(getPriceChange(token, timeRange))}
                        </td>
                        <td className="py-1 px-3 text-sm text-right text-gray-300 whitespace-nowrap">{formatUsd(getVolume(token, timeRange))}</td>
                        <td className="py-1 px-3 text-sm text-right text-gray-300 whitespace-nowrap">{formatNumber(getTradeCount(token, timeRange))}</td>
                        <td className="py-1 px-3 text-sm text-right text-gray-300 whitespace-nowrap">{formatUsd(token.liquidity)}</td>
                        <td className="py-1 px-3 text-sm text-right text-gray-300 whitespace-nowrap">{formatNumber(token.holder)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between py-1 px-3 bg-crypto-gray border border-gray-700 border-t-0 rounded-b-xl">
            <button onClick={() => setPage(page - 1)} disabled={page <= 1}
              className="px-3 py-1.5 text-sm rounded-lg bg-crypto-dark border border-gray-700 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              <i className="fas fa-chevron-left mr-1" /> Prev
            </button>
            <span className="text-sm text-gray-500">Page {page}</span>
            <button onClick={() => setPage(page + 1)} disabled={!hasNext}
              className="px-3 py-1.5 text-sm rounded-lg bg-crypto-dark border border-gray-700 text-gray-400 hover:text-white disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
              Next <i className="fas fa-chevron-right ml-1" />
            </button>
          </div>
          </>
        )}
      </main>
  );

  // Preview (gated) mode renders JUST the table — the parent TrendingPreviewGate
  // supplies a crisp, interactive Header/Footer OUTSIDE the blur layer so the
  // login modal (rendered by Header) isn't trapped in the dimmed background.
  if (preview) return body;

  return (
    <div className="min-h-screen bg-crypto-dark text-white">
      <Header />
      {body}
      <Footer />
    </div>
  );
};

export default TrendingPage;
